export const SHIPMENT_STATUSES = [
  'draft',
  'preparing',
  'in_transit',
  'arrived',
  'counting',
  'count_confirmed',
  'receiving',
  'completed',
] as const;

export type ShipmentStatusKey = (typeof SHIPMENT_STATUSES)[number];

export type TagColor =
  | 'success'
  | 'processing'
  | 'error'
  | 'warning'
  | 'default'
  | 'blue'
  | 'cyan'
  | 'green'
  | 'red'
  | 'orange';

export interface StatusMeta {
  label: string;
  color: TagColor;
}

export const SHIPMENT_STATUS_META: Record<ShipmentStatusKey, StatusMeta> = {
  draft: { label: 'Draft', color: 'default' },
  preparing: { label: 'Preparing', color: 'default' },
  in_transit: { label: 'In Transit', color: 'processing' },
  arrived: { label: 'Arrived', color: 'blue' },
  counting: { label: 'Counting', color: 'processing' },
  count_confirmed: { label: 'Count Confirmed', color: 'blue' },
  receiving: { label: 'Receiving', color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
};

export type ShipmentItemStatusKey = 'pending' | 'counted' | 'stored';

export const SHIPMENT_ITEM_STATUS_META: Record<ShipmentItemStatusKey, StatusMeta> = {
  pending: { label: 'Pending', color: 'default' },
  counted: { label: 'Counted', color: 'processing' },
  stored: { label: 'Stored', color: 'success' },
};

/** Legacy / alternate API strings → canonical shipment status. */
const STATUS_ALIASES: Record<string, ShipmentStatusKey> = {
  pending: 'draft',
  intransit: 'in_transit',
  countconfirmed: 'count_confirmed',
  received: 'completed',
  complete: 'completed',
};

export function normalizeShipmentStatus(status?: string): ShipmentStatusKey {
  if (!status) return 'draft';
  const key = status.toLowerCase().replace(/-/g, '_');
  if (key in SHIPMENT_STATUS_META) return key as ShipmentStatusKey;
  return STATUS_ALIASES[key] ?? 'draft';
}

export function getShipmentStatusMeta(status?: string): StatusMeta {
  return SHIPMENT_STATUS_META[normalizeShipmentStatus(status)];
}

export function getShipmentItemStatusMeta(status?: string): StatusMeta {
  const key = (status?.toLowerCase() ?? 'pending') as ShipmentItemStatusKey;
  if (key in SHIPMENT_ITEM_STATUS_META) return SHIPMENT_ITEM_STATUS_META[key];
  return SHIPMENT_ITEM_STATUS_META.pending;
}

/** Workflow step 0–3 for inbound detail stepper; -1 not used. */
export function getInboundWorkflowStepIndex(status?: string): number {
  switch (normalizeShipmentStatus(status)) {
    case 'draft':
    case 'preparing':
    case 'in_transit':
      return 0;
    case 'arrived':
    case 'counting':
      return 1;
    case 'count_confirmed':
    case 'receiving':
      return 2;
    case 'completed':
      return 3;
    default:
      return 0;
  }
}

export function canMarkShipmentArrived(status?: string): boolean {
  const normalized = normalizeShipmentStatus(status);
  return (
    normalized === 'draft' || normalized === 'preparing' || normalized === 'in_transit'
  );
}
